import React, { useEffect, useMemo } from "react";
import { UnitFormProps } from "./UnitFormProps";
import { Controller, useWatch } from "react-hook-form";
import {
  Grid,
  InputAdornment,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from "@material-ui/core";
import { Error } from "../common/Error";
import { Select } from "@ofqual/portal-components/components/Select";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  aoService,
  cachedLevelsService,
  sectorSubjectService,
} from "../../services";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { ItemType } from "@ofqual/portal-components/components/types";
import { UserType } from "../../redux/types";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { fieldLabels } from "./fieldLabels";

const useStyles = makeStyles((theme: Theme) => {
  return {
    formSection: {
      padding: 30,
      boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
      border: "1px solid #ddd",
      backgroundColor: "#fff",
      borderRadius: "3px",
    },
  };
});

export const UnitForm: React.FC<UnitFormProps> = ({
  control,
  errors,
  disabled,
  user: { userType, token },
  isEditForm,
  lookupTracker,
  setValue,
}) => {
  const classes = useStyles();

  const [sectorSubjectAreaLookups, levelsLookups, subLevelLookups] = useLookup(
    sectorSubjectService,
    {
      service: cachedLevelsService,
      id: "levelCode",
      label: "levelDescription",
    },
    {
      service: cachedLevelsService,
      id: "subLevelCode",
      label: "subLevelDescription",
    }
  );

  const groupedSectorSubjectAreas = toGroupedList(sectorSubjectAreaLookups, {
    id: "id",
    label: "label",
    group: "group",
  });

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoService.search(params || {}, { token }),
    }),
    [token]
  );
  const { unitLevel } = useWatch({
    control,
    name: ["unitLevel"],
  });

  useEffect(() => {
    setValue(
      "unitLevelId",
      levelsLookups.find((x) => x.id === unitLevel)?.levelId
    );
  }, [unitLevel, levelsLookups, setValue]);

  return (
    <form>
      <Typography variant="h6">Unit details</Typography>

      <Paper className={classes.formSection}>
        <Grid container spacing={3}>
          {userType === UserType.Ofqual ? (
            <Grid item xs={12}>
              <Controller
                control={control}
                rules={{ required: true }}
                name="organisationId"
                as={
                  <SearchField
                    searchFieldVariant="single"
                    label="Owner organisation"
                    datatest="unit-ao"
                    service={mappedOrgLookupService}
                    disabled={disabled}
                    readOnly={isEditForm}
                    required
                  />
                }
              />
              {errors.organisationId && <Error data-test="unit-ao-validation">This field is required</Error>}
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Controller
              control={control}
              name="title"
              rules={{ required: true }}
              as={
                <TextField
                  variant="outlined"
                  label={fieldLabels.title}
                  data-test="unit-title"
                  fullWidth
                  readOnly={disabled}
                  required
                />
              }
            />
            {errors.title?.type === "required" && (
              <Error data-test="unit-title-validation">This field is required</Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="aounitReferenceNumber"
              as={
                <TextField
                  variant="outlined"
                  label={fieldLabels.aounitReferenceNumber}
                  data-test="unit-organisation-reference-number"
                  fullWidth
                  readOnly={disabled}
                />
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="unitLevel"
              rules={{ required: true }}
              render={(props) => {
                const _onChange = (event: any) => {
                  setValue("unitSubLevel", "");
                  props.onChange(event);
                };
                return (
                  <Select
                    {...props}
                    label={fieldLabels.unitLevel}
                    readOnly={disabled}
                    lookupTracker={lookupTracker}
                    required
                    onChange={_onChange}
                    data-test="unit-level-field"
                  >
                    {levelsLookups.filter(
                      (value, index, self) =>
                        self.findIndex((x) => x.id === value.id) === index
                    )}
                  </Select>
                );
              }}
            />
            {errors.unitLevel?.type === "required" && (
              <Error data-test="unit-level-validation">This field is required</Error>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="unitSubLevel"
              rules={{ required: true }}
              as={
                <Select
                  label={fieldLabels.unitSubLevel}
                  disabled={!unitLevel}
                  readOnly={disabled}
                  lookupTracker={lookupTracker}
                  required
                  data-test="unit-sub-level-field"
                >
                  {subLevelLookups.filter((x) => x.levelCode === unitLevel)}
                </Select>
              }
            />
            {errors.unitSubLevel?.type === "required" && (
              <Error data-test="unit-sub-level-validation">This field is required</Error>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="unitCredit"
              as={
                <TextField
                  variant="outlined"
                  label={fieldLabels.unitCredit}
                  data-test="unit-credit-value"
                  fullWidth
                  readOnly={disabled}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="unitGLH"
              rules={{ required: true }}
              as={
                <TextField
                  variant="outlined"
                  label={fieldLabels.unitGLH}
                  data-test="guided-learning-hours"
                  fullWidth
                  readOnly={disabled}
                  required
                  type="number"
                  inputProps={{ min: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">hours</InputAdornment>
                    ),
                  }}
                />
              }
            />
            {errors.unitGLH?.type === "required" && (
              <Error data-test="unit-glh-validation">This field is required</Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="unitSectorSubjectArea"
              as={
                <Select
                  label={fieldLabels.unitSectorSubjectArea}
                  readOnly={disabled}
                  lookupTracker={lookupTracker}
                  datatest="subject-sector-area"
                >
                  {groupedSectorSubjectAreas as ItemType[]}
                </Select>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="learningOutcomes"
              rules={{ required: true }}
              as={
                <TextField
                  variant="outlined"
                  label={fieldLabels.learningOutcomes}
                  data-test="unit-learning-outcomes"
                  fullWidth
                  readOnly={disabled}
                  multiline
                  rows={5}
                  required
                />
              }
            />
            {errors.learningOutcomes?.type === "required" && (
              <Error data-test="unit-learning-outcomes-validation">This field is required</Error>
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="unitEndDate"
              as={
                <DatePicker
                  returnDayBoundary="startOfDay"
                  label={fieldLabels.unitEndDate}
                  readOnly={disabled}
                  dataTest="unit-unit-end-date"
                  clearable={true}
                />
              }
            />
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
};
